/**
 * Limits View
 */

define(function(require) {
    var _ = require('underscore');
    var config = require('config');
    var i18n = require('i18n');
    var BaseView = require('common/base.view');
    var Form = require('class/form.class');
    var userValidationConfig = require('modules/user/user.validation.config');
    var italianValidationConfig = require('modules/user/italian.validation.config');
    var LimitsModel = require('portlets/responsible-gaming/limits/limits.model');
    var validationHelpers = require('modules/user/user.validation.helpers');

    return BaseView.extend({

        events: {
            'focusout .fn-deposit-limits .fn-limit input': 'validateDepositLimitsAscendingOrder',
            'change .fn-deposit-limits .fn-limit select': 'validateDepositLimitsAscendingOrder',

            'submit .fn-form-session-timer': 'updateSessionTimer',
            'submit .fn-deposit-limits': 'updateDepositLimits',
            'tap .fn-confirm-deposit-limit': 'confirmDepositLimit'
        },

        initialize: function(options) {
            this.options = options;
            this.model = new LimitsModel(this.options.config);
        },

        subscribe: function() {
            this.listenTo(this.model, 'change', this.render);
        },

        render: function() {
            this.$el.html(this.tmpl('limits', this.model.toJSON()));

            var $formSessionTimer = this.$('.fn-form-session-timer');
            if ($formSessionTimer.length) {
                this.formSessionTimer = new Form($formSessionTimer);
            }

            var $formDepositLimits = this.$('.fn-deposit-limits');
            if ($formDepositLimits.length) {
                this.formDepositLimits = new Form($formDepositLimits, {
                    validationConfig: userValidationConfig(),
                    data: this.getFormData()
                });
            }

            this.validateAllLimitsAscendingOrder();

            return this;
        },

        getFormData: function() {
            var cfg = this.model.get('depositConfig');
            var allowedLimits = this.model.get('depositLimits').allowedLimits || this.model.get('allowedLimits');

            return {
                allowedLimits: allowedLimits,
                predefinedType: cfg.predefinedType,
                playerMustSetLimits: cfg.playerMustSetLimits,
                allPeriodsRequired: cfg.allPeriodsRequired,
                noLimitsAllowed: cfg.noLimitsAllowed,
                UNLIMITED_AMOUNT: cfg.UNLIMITED_AMOUNT
            };
        },

        updateDepositLimits: function(e) {
            e.preventDefault();

            if (!this.formDepositLimits.isDataChanged()) {
                this.showInfo({
                    content: this.translate('playtech.mobile.ngresponsiblegaming.limits.RGLimits.submitInitialFormData')
                });

                return;
            }

            this.formDepositLimits.validate().done(function(data, form) {
                form.showLoading();
                this.hideKeyboard();

                this.model.updateDepositLimits(data)
                    .done(this.successUpdateLimits.bind(this, data))
                    .fail(this.errorUpdateLimits.bind(this))
                    .always(form.hideLoading.bind(form));
            }.bind(this));
        },

        successUpdateLimits: function(data) {
            this.model.set(data);

            this.model.fetchInfo()
                .done(function() {
                    this.render();

                    this.showInfo({
                        content: this.translate('playtech.mobile.ngresponsiblegaming.limits.successUpdate')
                    });
                }.bind(this))
                .fail(this.renderNetworkError.bind(this));

            this.model.fetchDepositLimitInfo()
                .done(function() {
                    this.render();

                    this.showInfo({
                        content: this.translate('playtech.mobile.ngresponsiblegaming.limits.successUpdate')
                    });
                }.bind(this))
                .fail(this.renderNetworkError.bind(this));
        },

        errorUpdateLimits: function(response) {
            const { errorCode, userErrors = [] } = response.data;
            const errorMessage = userErrors[0]?.message;
            const message = i18n.tExt('playtech.mobile.ngresponsiblegaming.limits.error.' +
                (errorCode || 'default'), errorMessage);

            this.showInfo({
                content: message
            });

            this.render();
        },

        updateSessionTimer: function(e) {
            e.preventDefault();

            var form = this.formSessionTimer;
            var data = form.data();

            if (form.selectedData.sessionduration === data.sessionduration) {
                this.showInfo({
                    content: this.translate('playtech.mobile.ngresponsiblegaming.sessionTimer.submitInitialFormData')
                });

                return;
            }

            form.selectedData = _.clone(data);
            form.showLoading();

            this.model.updateSessionTimer(data)
                .done(this.successUpdateSessionTimer.bind(this, data))
                .fail(this.errorUpdateSessionTimer.bind(this))
                .always(form.hideLoading.bind(form));
        },

        successUpdateSessionTimer: function(data) {
            var sessionduration = Number(this.model.get('sessionduration'));
            var successMessage;

            this.model.set(data);

            if (sessionduration) {
                successMessage = this.translate('playtech.mobile.ngresponsiblegaming.limits.activated', sessionduration);
            } else {
                successMessage = this.translate('playtech.mobile.ngresponsiblegaming.limits.deactivated');
            }

            this.showInfo({
                content: successMessage
            });
        },

        errorUpdateSessionTimer: function(response) {
            this.render();

            if (response.data.errorCode === 1915 || response.data.errorCode === 1920) {
                this.model.set('sessionduration', this.model.get('waitingsessionduration'));
            }

            this.formSessionTimer.showErrorMessage({
                translation: 'playtech.mobile.ngresponsiblegaming.limits.error.' + (response.data.errorCode || 'default'),
                content: response.data.errorMessage
            });
        },

        _getValidationConfig: function() {
            return config.get('regulation') === 'italian'
                ? italianValidationConfig()
                : userValidationConfig();
        },

        confirmDepositLimit: function(event) {
            const { action, timePeriod } = event.target.dataset;
            this.$('.fn-pending-limits-messages').html('').addClass('hidden');

            if (!action || !timePeriod) {
                return;
            }

            this.model.acceptPlayerDepositLimits(action, timePeriod)
                .then(() => {
                    this.showInfo({
                        content: this.translate(`playtech.mobile.system.responsibleGaming.limits.${action}Success`)
                    });
                    this.model.fetchDepositLimitInfo();
                })
                .catch((error) => {
                    const { errorMessage, userErrors } = error?.response?.data || {};

                    this.$('.fn-pending-limits-messages').html(
                        userErrors?.[0]?.message
                        || errorMessage
                        || this.translate('playtech.mobile.ngresponsiblegaming.limits.generic.error')
                    ).removeClass('hidden');
                });
        },

        getLimitsValidationData: function(formData) {
            return Object.fromEntries(
                Object.entries(formData)
                    .map(([key, value]) => [this.getLimitName(key), value])
                    .filter(([key, value]) => key && Number(String(value).replace(',', '.')))
                    .map(([key, value]) => [key, Number(String(value).replace(',', '.'))])
            );
        },

        getLimitName: function(fieldName) {
            var validationFieldTypesMatchStr = 'session|day|week|month';

            return fieldName?.match(validationFieldTypesMatchStr)?.[0];
        },

        validateDepositLimitsAscendingOrder: function() {
            this.validateLimitsAscendingOrder(this.formDepositLimits);
        },

        validateLimitsAscendingOrder: function(form) {
            const validationData = this.getLimitsValidationData(form.data());
            const validations = Object.keys(validationData)
                .map((limitName) => validationHelpers.isAscendingLimitAmount[limitName])
                .filter(Boolean);

            if (!validations.length) {
                return;
            }

            const allValid = validations.every((validation) => validation(validationData));

            if (allValid) {
                form.hideWarningMessages();
            } else {
                form.showWarningMessage({ translation: 'playtech.mobile.system.tooltip.limits.ascending.warning' });
            }
        },

        validateAllLimitsAscendingOrder: function() {
            if (this.formDepositLimits) {
                this.validateLimitsAscendingOrder(this.formDepositLimits);
            }
        }
    });
});
